import { Button, Card, Empty, Input, List, Space, Spin, Tag, Typography, message } from 'antd';
import React, { useState } from 'react';
import {
  useCreateVendorNotificationEmail,
  useDeleteVendorNotificationEmail,
  useVendorNotificationEmailList,
} from 'hooks/useVendorNotificationEmail';

const { Text } = Typography;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface VendorNotificationEmailManagerProps {
  userId: number;
}

export const VendorNotificationEmailManager: React.FC<VendorNotificationEmailManagerProps> = ({
  userId,
}) => {
  const [newEmail, setNewEmail] = useState('');

  const { data, isFetching } = useVendorNotificationEmailList(userId);
  const createMutation = useCreateVendorNotificationEmail();
  const deleteMutation = useDeleteVendorNotificationEmail();

  const emails = data?.results ?? [];

  const handleAdd = async () => {
    const email = newEmail.trim();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) {
      message.error('Enter a valid email address');
      return;
    }
    if (emails.some(e => e.email.toLowerCase() === email.toLowerCase())) {
      message.error('Already registered');
      return;
    }
    try {
      await createMutation.mutateAsync({ user_id: userId, email });
      message.success(`Added ${email}`);
      setNewEmail('');
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string; email?: string[] } } };
      const detail = err?.response?.data?.detail ?? err?.response?.data?.email?.[0];
      message.error(detail ?? 'Failed to add email');
    }
  };

  const handleDelete = async (id: number, email: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success(`Removed ${email}`);
    } catch {
      message.error('Failed to remove email');
    }
  };

  return (
    <Card size="small" title="Notification emails" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Text type="secondary" style={{ fontSize: 12 }}>
          Notifications are sent to these emails when SampleVideo completes.
        </Text>

        {isFetching && emails.length === 0 ? (
          <Spin size="small" />
        ) : emails.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No notification email registered"
          />
        ) : (
          <List
            size="small"
            dataSource={emails}
            renderItem={item => (
              <List.Item
                actions={[
                  <Button
                    danger
                    size="small"
                    type="text"
                    key="del"
                    loading={
                      deleteMutation.isPending && deleteMutation.variables === item.id
                    }
                    onClick={() => handleDelete(item.id, item.email)}
                  >
                    Remove
                  </Button>,
                ]}
              >
                <Space>
                  <Tag color="blue">EMAIL</Tag>
                  <Text>{item.email}</Text>
                </Space>
              </List.Item>
            )}
          />
        )}

        <Space.Compact style={{ width: '100%' }}>
          <Input
            type="email"
            placeholder="user@example.com"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            onPressEnter={handleAdd}
            disabled={createMutation.isPending}
          />
          <Button
            type="primary"
            onClick={handleAdd}
            loading={createMutation.isPending}
            disabled={!newEmail.trim()}
          >
            Add
          </Button>
        </Space.Compact>
      </Space>
    </Card>
  );
};
